import { DocumentReaderResponseType } from '@regulaforensics/vp-frontend-document-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'shared/components/alerts';
import { StepAction } from '../../types';
import { ensureRegulaSdkInitialized } from '../../../../regula-sdk/regula-sdk';
import {
  DocumentImagesResult,
  DocumentReaderElement,
  DocumentReaderEvent,
  ManualScanSide,
  ManualScanStatus,
} from '../types';
import { getManualIdScanSettings } from '../utils/get-manual-id-scan-settings';
import { extractSingleDocumentImage } from '../utils/extract-single-document-image';
import { isRecognizedDocument } from '../utils/is-recognized-document';

export const useIdScanManualStep = (onSuccess: (images: DocumentImagesResult) => void) => {
  const elementRef = useRef<DocumentReaderElement | null>(null);
  const [status, setStatus] = useState<ManualScanStatus>('loading-sdk');
  const [activeSide, setActiveSide] = useState<ManualScanSide | null>(null);
  const [front, setFront] = useState<string | undefined>(undefined);
  const [rear, setRear] = useState<string | undefined>(undefined);
  const [hasRecognizedSide, setHasRecognizedSide] = useState(false);

  const { t, i18n } = useTranslation('web-common');
  const { alertError } = useAlert();

  const processScanResult = useCallback(
    (response: DocumentReaderResponseType) => {
      const image = extractSingleDocumentImage(response);

      if (!image || !activeSide) {
        return false;
      }

      const nextFront = activeSide === 'front' ? image : front;
      const nextRear = activeSide === 'rear' ? image : rear;
      const nextHasRecognizedSide =
        hasRecognizedSide || isRecognizedDocument(response.status?.detailsOptical);

      setFront(nextFront);
      setRear(nextRear);
      setHasRecognizedSide(nextHasRecognizedSide);
      setActiveSide(null);

      if (nextFront && nextRear) {
        if (nextHasRecognizedSide) {
          onSuccess({ front: nextFront, rear: nextRear });
        } else {
          alertError(new Error(t('id-submission.id-scan.invalid-document.message')));
        }
      }

      return true;
    },
    [activeSide, front, rear, hasRecognizedSide, onSuccess, alertError, t]
  );

  const handleRegulaEvent = useCallback(
    (event: DocumentReaderEvent) => {
      const { action, data } = event.detail;

      if (action === StepAction.PROCESS_FINISHED) {
        if (data.status === 1 && data.response && processScanResult(data.response)) {
          return;
        }

        setActiveSide(null);
        alertError(new Error(t('id-submission.id-scan.couldnt-read-side.message')));
      }

      if (action === StepAction.CLOSE) {
        setActiveSide(null);
        alertError(new Error(t('id-submission.id-scan.scan-cancelled.message')));
      }
    },
    [processScanResult, alertError, t]
  );

  useEffect(() => {
    let cancelled = false;

    ensureRegulaSdkInitialized()
      .then(() => {
        if (cancelled) {
          return;
        }
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setStatus('error');
        alertError(new Error(t('id-submission.id-scan.scanner-failed-to-load.message')));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!activeSide || !element) {
      return undefined;
    }

    const language = i18n.language;

    element.settings = {
      ...getManualIdScanSettings(),
      locale: language,
    };

    element.addEventListener('document-reader', handleRegulaEvent);

    return () => {
      element.removeEventListener('document-reader', handleRegulaEvent);
    };
  }, [activeSide, handleRegulaEvent, i18n.language]);

  const startCapture = useCallback(
    (side: ManualScanSide) => {
      if (status !== 'ready') {
        return;
      }
      setActiveSide(side);
    },
    [status]
  );

  return {
    elementRef,
    status,
    activeSide,
    front,
    rear,
    startCapture,
  };
};
